import { Link } from 'react-router-dom';
import { site } from '../../site';
import { LegalPage } from './LegalPage';

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy policy"
      documentTitle="Privacy — dala"
      updated={site.lastUpdated}
    >
      <p>
        This policy explains how {site.operator} (“Dala”, “we”, “us”) collects,
        uses, and shares information when you use the Dala mobile app (Android
        package <code>{site.packageId}</code>) and this website. Dala currently
        operates in {site.region}, {site.country}.
      </p>
      <p>
        Questions: see{' '}
        <Link to="/support" className="font-bold text-terracotta hover:underline">
          Support
        </Link>{' '}
        or email{' '}
        <a
          href={`mailto:${site.privacyEmail}`}
          className="font-bold text-terracotta hover:underline"
        >
          {site.privacyEmail}
        </a>
        .
      </p>

      <Section title="Who we are">
        <p>
          Dala is a local marketplace for rides (sakay), food (pagkaon),
          stores (tindahan), shop-for-me (papalit), and parcels (padala) across
          MABAMA — Magsaysay, Bansalan, and Matanao. We process personal data as
          a personal information controller under the Philippine Data Privacy Act
          of 2012 (Republic Act No. 10173).
        </p>
      </Section>

      <Section title="Information we collect">
        <p>Depending on how you use Dala, we may collect:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-ink">Account.</strong> Mobile number (used to
            sign in with an SMS code), name, optional email, optional profile
            photo, language preference (Bisaya, Tagalog, or English), and role
            (customer, driver, merchant, or staff).
          </li>
          <li>
            <strong className="text-ink">Location.</strong> Precise GPS location
            while you book or take a ride, send or receive a parcel, place a
            food or store order, or go online as a driver. We also store pickup,
            drop-off, and saved-place addresses you enter.
          </li>
          <li>
            <strong className="text-ink">Orders and trips.</strong> Routes,
            vehicle type, fares, item lists, photos you send of store goods,
            order status, ratings, and cancellation reasons.
          </li>
          <li>
            <strong className="text-ink">Messages and calls.</strong> In-app chat
            (including photos) with drivers, stores, or Dala support, and
            metadata for voice or video calls used to coordinate a job.
          </li>
          <li>
            <strong className="text-ink">Driver and merchant applications.</strong>{' '}
            Information and documents you submit to drive or sell on Dala (for
            example identity and vehicle details) so we can review your
            application.
          </li>
          <li>
            <strong className="text-ink">Device.</strong> Push-notification tokens,
            approximate device identifiers needed to deliver messages, and basic
            app diagnostics.
          </li>
        </ul>
        <p>
          The MABAMA pilot settles fares and orders in cash. Dala does not
          collect card numbers or e-wallet credentials for payments in the app
          today.
        </p>
      </Section>

      <Section title="How we use information">
        <ul className="list-disc space-y-2 pl-5">
          <li>Create and secure your account, including SMS sign-in.</li>
          <li>
            Match customers with drivers and stores, show live job progress, and
            calculate fares.
          </li>
          <li>
            Show maps and routes, and keep service inside the MABAMA pilot
            area.
          </li>
          <li>Send order, chat, and support notifications.</li>
          <li>Review driver and merchant applications and keep the network safe.</li>
          <li>Respond to support requests and investigate disputes or abuse.</li>
          <li>Improve reliability and understand how the pilot is used.</li>
          <li>Comply with law and protect Dala, users, and the public.</li>
        </ul>
      </Section>

      <Section title="Location">
        <p>
          Precise location is required to provide rides, deliveries, and driver
          matching. Customers share a pickup and destination; drivers share
          location while online or on a job so the other party can see progress.
          You can turn location off in system settings, but core features will
          not work without it.
        </p>
      </Section>

      <Section title="Who we share information with">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-ink">Other users on a job.</strong> A driver
            or store sees the name, photo, pickup/drop-off, and chat needed to
            complete your request. You see similar details about them.
          </li>
          <li>
            <strong className="text-ink">Service providers.</strong> We use Google
            Firebase (Authentication, Firestore, Cloud Storage, Cloud Functions,
            Cloud Messaging, Hosting) and Google Maps / geocoding to run the
            app. Voice and video calls use Agora. These providers process data
            on our instructions.
          </li>
          <li>
            <strong className="text-ink">Legal.</strong> We may disclose
            information if required by law or to protect safety and rights.
          </li>
        </ul>
        <p>We do not sell personal information.</p>
      </Section>

      <Section title="Retention">
        <p>
          We keep account, trip, order, and chat records for as long as your
          account is active and for a reasonable period afterwards so we can
          handle support, safety, and legal obligations. You can delete your
          account at any time from Account settings in the app, or via{' '}
          <Link to="/support" className="font-bold text-terracotta hover:underline">
            Support
          </Link>
          . Deleting your account starts a 14-day window in which you can
          change your mind; after that, your profile and saved places are
          removed and your name, address, and contact details are stripped
          from past trips and orders. We keep the trip and order records
          themselves — the fare, dates, and status — because accounting and
          dispute records must be retained where the law requires it.
        </p>
      </Section>

      <Section title="Security">
        <p>
          Access to operations data is limited to authorised Dala staff. Client
          access to Firebase is restricted by security rules; staff use a
          separate console. No method of transmission or storage is completely
          secure, and we cannot guarantee absolute security.
        </p>
      </Section>

      <Section title="Children">
        <p>
          Dala is not directed at children under 13, and we do not knowingly
          collect personal information from them. If you believe a child has
          created an account, contact us and we will delete it.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          Subject to the Data Privacy Act, you may request access, correction, or
          deletion of personal data we hold, or withdraw consent where processing
          is based on consent. Account settings in the app lets you download a
          copy of your data and delete your account yourself, without waiting
          on a support request. For anything else, contact{' '}
          <a
            href={`mailto:${site.privacyEmail}`}
            className="font-bold text-terracotta hover:underline"
          >
            {site.privacyEmail}
          </a>
          . You may also complain to the National Privacy Commission.
        </p>
      </Section>

      <Section title="This website">
        <p>
          The public pages on this site (including this policy and the support
          page) do not require an account. The staff console at{' '}
          <code>/admin</code> is for authorised operators only and uses email
          sign-in.
        </p>
      </Section>

      <Section title="Changes">
        <p>
          We may update this policy. The “Last updated” date at the top will
          change, and the current version will always be posted at this URL.
        </p>
      </Section>
    </LegalPage>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl text-ink">{title}</h2>
      {children}
    </section>
  );
}
